import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  Paper,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Award,
  Shield,
  CheckCircle,
  Brain,
  Leaf,
  Code,
  BarChart3,
  ArrowRight,
  Calendar,
  Download,
  ExternalLink,
  Eye,
  GraduationCap,
  Share2,
  Search,
  XCircle,
  Lock,
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const useAuthToken = () => {
  try {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('token') ||
      null
    );
  } catch {
    return null;
  }
};

// ─── Certificate Card ─────────────────────────────────────────────────────────
const CertificateCard = ({ cert, onDownload, onShare, onView, downloading }) => {
  const certId = cert._id || cert.id || cert.certificateId;
  const courseName = cert.courseName || cert.courseTitle || cert.course?.title || 'Unknown Course';
  const studentName = cert.studentName || cert.userName || cert.user?.name || '';
  const completedAt = cert.completedAt || cert.issuedAt || cert.createdAt;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{ height: '100%' }}
    >
      <Card
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.025)',
          border: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'border-color 0.25s, box-shadow 0.25s, transform 0.25s',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.14)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          },
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, #7c3aed, #3b82f6)', flexShrink: 0 }} />

        {/* Header area */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(124,58,237,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Award size={22} color="#a78bfa" />
            </Box>
            <Chip
              label="Verified"
              size="small"
              icon={<CheckCircle size={12} />}
              sx={{
                bgcolor: 'rgba(16,185,129,0.1)',
                color: '#10b981',
                fontWeight: 700,
                border: '1px solid rgba(16,185,129,0.2)',
                '& .MuiChip-icon': { color: '#10b981' },
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mt: 2, mb: 0.5, fontSize: '1rem', lineHeight: 1.4, color: '#fafafa' }}
          >
            {courseName}
          </Typography>
          {studentName && (
            <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
              {studentName}
            </Typography>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, px: 3, py: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={13} color="#71717a" />
              <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                Completed {formatDate(completedAt)}
              </Typography>
            </Box>
            {certId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Shield size={13} color="#71717a" />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#71717a',
                    fontFamily: 'monospace',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.68rem',
                  }}
                >
                  {String(certId).slice(0, 20)}…
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloading === certId ? (
                <CircularProgress size={12} sx={{ color: '#a78bfa' }} />
              ) : (
                <Download size={13} />
              )
            }
            onClick={() => onDownload(cert)}
            disabled={downloading === certId}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.78rem',
              borderColor: 'rgba(255,255,255,0.1)',
              color: '#a1a1aa',
              '&:hover': { borderColor: '#a78bfa', color: '#a78bfa', bgcolor: 'rgba(124,58,237,0.08)' },
            }}
          >
            Download
          </Button>
          <Tooltip title="Copy verification link">
            <IconButton
              size="small"
              onClick={() => onShare(cert)}
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1.5,
                color: '#71717a',
                '&:hover': { color: '#a1a1aa', borderColor: 'rgba(255,255,255,0.18)' },
              }}
            >
              <Share2 size={13} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View public verification">
            <IconButton
              size="small"
              onClick={() => onView(cert)}
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1.5,
                color: '#71717a',
                '&:hover': { color: '#a1a1aa', borderColor: 'rgba(255,255,255,0.18)' },
              }}
            >
              <Eye size={13} />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    </motion.div>
  );
};

// ─── Certificate Skeleton ─────────────────────────────────────────────────────
const CertificateSkeleton = () => (
  <Skeleton
    variant="rectangular"
    height={200}
    sx={{ borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)' }}
  />
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = () => (
  <Box sx={{ py: 10, textAlign: 'center' }}>
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px dashed rgba(255,255,255,0.09)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 3,
      }}
    >
      <Award size={36} color="#3f3f46" />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fafafa' }}>
      No certificates yet
    </Typography>
    <Typography variant="body2" sx={{ color: '#71717a', mb: 3, maxWidth: 360, mx: 'auto' }}>
      Complete a course to earn your first certificate.
    </Typography>
    <Button
      variant="outlined"
      component={Link}
      to="/courses"
      endIcon={<ArrowRight size={15} />}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        borderColor: 'rgba(255,255,255,0.12)',
        color: '#a1a1aa',
        '&:hover': { borderColor: '#a78bfa', color: '#a78bfa', bgcolor: 'rgba(124,58,237,0.08)' },
      }}
    >
      Explore Courses
    </Button>
  </Box>
);

// ─── Verify Tab ───────────────────────────────────────────────────────────────
const VerifyTab = () => {
  const navigate = useNavigate();
  const [certId, setCertId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const handleVerify = async () => {
    if (!certId.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/certificates/verify/${certId.trim()}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.valid) {
        setVerifyResult({ valid: true, ...data });
      } else {
        setVerifyResult({ valid: false, message: data?.message || 'Certificate not found or revoked.' });
      }
    } catch {
      setVerifyResult({ valid: false, message: 'Unable to verify. Please try again.' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', py: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75, color: '#fafafa', textAlign: 'center' }}>
        Verify a Certificate
      </Typography>
      <Typography variant="body2" sx={{ color: '#71717a', mb: 4, textAlign: 'center' }}>
        Enter a certificate ID to confirm its authenticity.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="e.g. QN-CERT-2024-ABC123"
          value={certId}
          onChange={(e) => setCertId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Award size={16} color="#52525b" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.04)',
              borderRadius: 2,
              color: '#fafafa',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
              '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
            },
            '& input::placeholder': { color: '#52525b' },
          }}
        />
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={verifying || !certId.trim()}
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 700,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            bgcolor: '#7c3aed',
            '&:hover': { bgcolor: '#6d28d9' },
          }}
        >
          {verifying ? (
            <CircularProgress size={18} sx={{ color: 'white' }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Search size={15} />
              Verify
            </Box>
          )}
        </Button>
      </Box>

      <AnimatePresence mode="wait">
        {verifyResult && (
          <motion.div
            key={verifyResult.valid ? 'valid' : 'invalid'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {verifyResult.valid ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  border: '1px solid rgba(16,185,129,0.22)',
                  bgcolor: 'rgba(16,185,129,0.05)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle size={20} color="#10b981" />
                  <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem' }}>
                    Certificate Verified
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {[
                    { label: 'Course', value: verifyResult.courseName || 'N/A' },
                    { label: 'Graduate', value: verifyResult.studentName || verifyResult.userName || 'N/A' },
                    { label: 'Completed', value: formatDate(verifyResult.completedAt || verifyResult.issuedAt) },
                    { label: 'Issued by', value: 'QoreNova Academy' },
                  ].map((item, i) => (
                    <Box key={i}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#71717a', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.06)' }} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/verify/${certId}`)}
                  startIcon={<ExternalLink size={13} />}
                  sx={{
                    borderColor: 'rgba(16,185,129,0.35)',
                    color: '#10b981',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    '&:hover': { bgcolor: 'rgba(16,185,129,0.08)', borderColor: '#10b981' },
                  }}
                >
                  View Full Certificate
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  border: '1px solid rgba(239,68,68,0.22)',
                  bgcolor: 'rgba(239,68,68,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <XCircle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
                <Box>
                  <Typography sx={{ color: '#ef4444', fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                    Not Verified
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                    {verifyResult.message}
                  </Typography>
                </Box>
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

// ─── Certificate Tracks (static) ─────────────────────────────────────────────
const TRACKS = [
  { icon: <Brain size={26} />, title: 'AI & Machine Learning', count: 12, color: '#a78bfa', bg: 'rgba(124,58,237,0.08)' },
  { icon: <Leaf size={26} />, title: 'Green Technology', count: 8, color: '#34d399', bg: 'rgba(16,185,129,0.08)' },
  { icon: <BarChart3 size={26} />, title: 'Data Science', count: 10, color: '#60a5fa', bg: 'rgba(59,130,246,0.08)' },
  { icon: <Code size={26} />, title: 'Engineering & Dev', count: 15, color: '#fbbf24', bg: 'rgba(245,158,11,0.08)' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
const Certifications = () => {
  const navigate = useNavigate();
  const token = useAuthToken();

  const [tab, setTab] = useState(0);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '' });

  const fetchMyCerts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/certificates/my`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const list = Array.isArray(data) ? data : data?.certificates || data?.data || [];
        setCerts(list);
      } else if (res.status === 401) {
        setError('Please log in to view your certificates.');
      } else {
        setError(data?.message || 'Failed to load certificates.');
      }
    } catch {
      setError('Unable to connect. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab === 0 && token) fetchMyCerts();
  }, [tab, token, fetchMyCerts]);

  const handleDownload = async (cert) => {
    const certId = cert._id || cert.id || cert.certificateId;
    if (!certId || !token) return;
    setDownloading(certId);
    try {
      const res = await fetch(`${API_BASE}/api/certificates/download/${certId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, msg: 'Certificate downloaded!' });
    } catch {
      setSnackbar({ open: true, msg: 'Download failed. Please try again.' });
    } finally {
      setDownloading(null);
    }
  };

  const handleShare = (cert) => {
    const certId = cert._id || cert.id || cert.certificateId;
    const url = `${window.location.origin}/verify/${certId}`;
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({ open: true, msg: 'Verification link copied!' });
    }).catch(() => {
      setSnackbar({ open: true, msg: `Link: ${url}` });
    });
  };

  const handleView = (cert) => {
    const certId = cert._id || cert.id || cert.certificateId;
    navigate(`/verify/${certId}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#09090b' }}>
      <Header />

      {/* ── HERO ── */}
      <Box
        sx={{
          bgcolor: '#09090b',
          pt: { xs: 10, md: 14 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.6,
                borderRadius: 6,
                border: '1px solid rgba(124,58,237,0.28)',
                bgcolor: 'rgba(124,58,237,0.07)',
                mb: 3,
              }}
            >
              <GraduationCap size={13} color="#a78bfa" />
              <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, letterSpacing: 0.8 }}>
                CERTIFICATIONS
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{ color: '#fafafa', fontWeight: 800, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.3rem' } }}
            >
              Your Certificates
            </Typography>
            <Typography variant="body1" sx={{ color: '#a1a1aa', mb: 4, maxWidth: 460, mx: 'auto' }}>
              Download, share, and verify your earned credentials.
            </Typography>

            {!token && (
              <Button
                variant="contained"
                component={Link}
                to="/login"
                endIcon={<ArrowRight size={15} />}
                sx={{
                  bgcolor: '#7c3aed',
                  borderRadius: 2,
                  py: 1.25,
                  px: 4,
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#6d28d9' },
                }}
              >
                Sign in to View Certificates
              </Button>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>

        {/* ── Tabs panel ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            mb: 6,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                py: 2.5,
                color: '#71717a',
                '&.Mui-selected': { color: '#fafafa' },
              },
              '& .MuiTabs-indicator': { bgcolor: '#7c3aed' },
            }}
          >
            <Tab label={`My Certificates${!token ? ' 🔒' : ''}`} />
            <Tab label="Verify Certificate" />
          </Tabs>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <AnimatePresence mode="wait">
              {tab === 0 && (
                <motion.div key="my-certs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {!token ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Lock size={44} color="#3f3f46" style={{ marginBottom: 16 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fafafa' }}>
                        Sign in to view your certificates
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#71717a', mb: 3 }}>
                        Log in or create an account to track your certifications.
                      </Typography>
                      <Button
                        variant="contained"
                        component={Link}
                        to="/login"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          bgcolor: '#7c3aed',
                          '&:hover': { bgcolor: '#6d28d9' },
                        }}
                      >
                        Sign In
                      </Button>
                    </Box>
                  ) : loading ? (
                    <Grid container spacing={3}>
                      {[1, 2, 3].map((i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                          <CertificateSkeleton />
                        </Grid>
                      ))}
                    </Grid>
                  ) : error ? (
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'rgba(239,68,68,0.06)',
                        border: '1px solid rgba(239,68,68,0.18)',
                        color: '#fca5a5',
                        '& .MuiAlert-icon': { color: '#ef4444' },
                      }}
                      action={
                        <Button size="small" onClick={fetchMyCerts} sx={{ color: '#fca5a5' }}>
                          Retry
                        </Button>
                      }
                    >
                      {error}
                    </Alert>
                  ) : certs.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#fafafa' }}>
                          {certs.length} Certificate{certs.length !== 1 ? 's' : ''} Earned
                        </Typography>
                        <Chip
                          label={`${certs.length} total`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'rgba(124,58,237,0.12)',
                            color: '#a78bfa',
                            border: '1px solid rgba(124,58,237,0.22)',
                          }}
                        />
                      </Box>
                      <Grid container spacing={3}>
                        {certs.map((cert, i) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cert._id || cert.id || i}>
                            <CertificateCard
                              cert={cert}
                              onDownload={handleDownload}
                              onShare={handleShare}
                              onView={handleView}
                              downloading={downloading}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </motion.div>
              )}

              {tab === 1 && (
                <motion.div key="verify" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <VerifyTab />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Paper>

        {/* ── Certificate Tracks ── */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#fafafa' }}>
            Certificate Tracks
          </Typography>
          <Grid container spacing={2}>
            {TRACKS.map((track, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: 2.5,
                    cursor: 'pointer',
                    transition: 'border-color 0.25s, box-shadow 0.25s, transform 0.25s',
                    '&:hover': {
                      borderColor: `${track.color}44`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${track.color}14`,
                    },
                  }}
                  onClick={() => navigate('/courses')}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: track.bg,
                      color: track.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {track.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#fafafa' }}>
                    {track.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1.5 }}>
                    {track.count} courses available
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: track.color }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Explore</Typography>
                    <ArrowRight size={11} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── How it Works ── */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#fafafa' }}>
            How Certification Works
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 2 }}>
            {[
              { num: '01', title: 'Enroll & Learn', desc: 'Complete all course modules and pass assessments.', color: '#a78bfa' },
              { num: '02', title: 'Pass Assessment', desc: 'Score 80%+ on the final exam to earn your certificate.', color: '#60a5fa' },
              { num: '03', title: 'Download & Share', desc: 'Receive a verified PDF certificate you can share anywhere.', color: '#34d399' },
            ].map((step, i) => (
              <Box
                key={i}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: 12,
                    fontSize: '4rem',
                    fontWeight: 900,
                    opacity: 0.04,
                    lineHeight: 1,
                    color: step.color,
                    userSelect: 'none',
                  }}
                >
                  {step.num}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, color: step.color, mb: 1, fontSize: '1.4rem' }}>
                  {step.num}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#fafafa' }}>
                  {step.title}
                </Typography>
                <Typography variant="caption" sx={{ color: '#71717a', lineHeight: 1.6, display: 'block' }}>
                  {step.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      <Footer />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ open: false, msg: '' })}
        message={snackbar.msg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Certifications;
